/**
 * 渠道趋势折线图/柱图子组件
 * 对应板块: 板块 D — 在投广告渠道分析
 */
import React, { useMemo } from 'react';
import EChartCommon, { getAxisPointer } from '@op/components/EChartCommon';
import {
  formatChartParamsDate,
  listTimeTitleFunc,
  channelShowElement,
} from '@op/components/ChartCommon/formatter';
import { DBO_EC, DBO_V } from '@op/utils/format';
import { channelColorMap, chartAllColors } from '@op/constants';
import {
  channelInfo,
  channelLegendRichInfo,
  dealwithChannelChartKey,
} from '@op/components/ChannelLabel';
import {
  getChartPosition,
  formatTimeXAxisText,
  getXAxisAxisLabel,
} from '@op/utils/chart';
import type { TRequestBackType } from '@op/utils/requestUtil';
import type { TYAxisFormat } from './types';
import useLanguage from '@op/hooks/useLanguage';
import { dealWithOthersName } from '@op/utils/languageValue';

interface IChannelTrendLineChartProps {
  data: TRequestBackType;
  loading?: boolean;
  chartType: 'line' | 'bar';
  yAxisFormat: TYAxisFormat;
  filter?: any;
  height: number;
}

const ChannelTrendLineChart: React.FC<IChannelTrendLineChartProps> = ({
  data,
  loading,
  chartType,
  yAxisFormat,
  filter,
  height,
}) => {
  const { formatMessage } = useLanguage();
  // ==================== Y轴格式化 ====================
  const yAxisFormatter = useMemo(() => {
    const formatters: Record<
      TYAxisFormat,
      (v: number) => string | number | undefined
    > = {
      integer: (v) => DBO_EC.reviews(v),
      percent: (v) => DBO_EC.percent(v),
    };
    return formatters[yAxisFormat];
  }, [yAxisFormat]);

  // ==================== 系列颜色配置 ====================
  const seriesWithColor = useMemo(() => {
    if (!data?.series?.length) return [];

    return data.series.map((item, index) => ({
      ...item,
      itemStyle: {
        color: channelColorMap[item.name] || chartAllColors[index],
      },
    }));
  }, [data]);

  // percent 类型不展示占比
  const isPercent = yAxisFormat === 'percent';

  // ==================== 图表配置 ====================
  const chartOption = useMemo(() => {
    if (data?.isEmpty) return {};
    const xAxisData = data.xAxis || [];

    const series = seriesWithColor.map((item) => ({
      name: item.name,
      type: chartType === 'bar' ? 'bar' : 'line',
      data: item.data.map((v: number) => v || undefined),
      stack: chartType === 'bar' ? 'total' : undefined,
      itemStyle: item.itemStyle,
    }));

    // 创建图例富文本样式
    const rich = channelLegendRichInfo(16, 16, undefined, true);
    return {
      tooltip: {
        trigger: 'axis',
        appendToBody: false,
        confine: false,
        position: (pos: any, params: any, dom: any, rect: any, size: any) =>
          getChartPosition(pos, params, dom, rect, size, -5),
        formatter: (params: any[]) => {
          const currentDateTotal = params?.reduce(
            (sum: number, item: any) => sum + (item?.value || 0),
            0,
          );
          const formatValue = isPercent ? DBO_V.ratio_min : DBO_V.reviews;
          return formatChartParamsDate({
            params,
            total: currentDateTotal,
            isSort: true,
            valueFormat: (value: number) => formatValue(value),
            textShowFun: (marker, seriesName) => {
              return channelShowElement(
                marker,
                dealWithOthersName(seriesName, 'channel'),
                undefined,
                true,
              );
            },
            dealwithRecords(records, total) {
              const newRecords: Array<any> = records.map((item) => {
                // percent 类型不展示占比
                if (isPercent) {
                  return item;
                }
                const share = DBO_V.percentage(item.originValue, total, 2);
                return {
                  ...item,
                  value: `${item.value}${!item.originValue ? '' : ` (${share})`}`,
                };
              });
              if (!isPercent) {
                newRecords.unshift({
                  text: formatMessage('Sum'),
                  value: DBO_V.reviews(total),
                  originValue: total,
                  originText: '',
                });
              }
              return newRecords.filter((item) => Boolean(item));
            },
            tablesConfigFunc: (records: any) => {
              const columns = [
                {
                  key: 'text',
                  header: listTimeTitleFunc(params[0]?.name, filter?.date_type),
                },
                {
                  key: 'value',
                  align: 'right',
                  weight: 'bold',
                  header: '',
                },
              ];
              return [
                {
                  showHeader: true,
                  records,
                  columns,
                },
              ];
            },
          });
        },
      },
      grid: {
        top: 20,
        left: 5,
        right: 5,
        contentLabel: true,
      },
      legend: {
        data: data.legend,
        formatter: (name: string) => {
          const realKey = dealwithChannelChartKey(name.toLocaleLowerCase());
          return `{${realKey}|} {name|${dealWithOthersName(name, 'channel')}}`;
        },
        textStyle: {
          rich: {
            name: { align: 'left', padding: [2, 0, 0, 0] },
            ...rich,
          },
        },
      },
      axisPointer: DBO_EC.axisPointer((data: any) =>
        DBO_EC.timeX(data.value, filter?.date_type),
      ),
      xAxis: {
        type: 'category',
        data: xAxisData,
        boundaryGap: chartType === 'bar' ? ['20%', 0] : ['20%', '20%'],
        axisLabel: {
          formatter: (value: string) =>
            DBO_EC.timeX(value, filter?.date_type || 'daily'),
        },
      },
      yAxis: {
        type: 'value',
        axisLabel: {
          formatter: yAxisFormatter,
        },
      },
      series,
    };
  }, [data, seriesWithColor, chartType, yAxisFormatter, filter, formatMessage]);

  // ==================== 渲染 ====================
  return (
    <div className="dbo-channel-trend-chart">
      <EChartCommon
        options={chartOption || {}}
        optionsProps={{ type: chartType }}
        height={height}
        empty={data?.isEmpty}
        loading={loading}
      />
    </div>
  );
};

export default React.memo(ChannelTrendLineChart);
